<template>
  <div class="flex-1 w-full relative">
    <button
      ref="triggerRef"
      @click.prevent="toggleDropdown"
      :disabled="disabled"
      tabindex="0"
      class="flex items-center justify-between px-3 py-2.5 rounded-md border border-gray-200 shadow-soft bg-white hover:bg-gray-50 appearance-none text-blue-600 hover:text-blue-400 font-bold w-full cursor-pointer transition-colors ease-out duration-100 min-w-0 focus:outline-none focus:ring-2 focus:ring-blue-400"
      :class="{
        'opacity-50 cursor-not-allowed': disabled,
      }"
    >
      <span class="leading-tight flex-1 truncate text-left">
        <template v-if="selectedOption">
          <slot name="option" v-bind="{option: selectedOption}">
            {{ selectedOption.label }}
          </slot>
        </template>
        <template v-else>
          <span class="text-gray-500">{{ placeholder || 'Select an option...' }}</span>
        </template>
      </span>
      <span class="shrink-0 pointer-events-none flex items-center text-blue-500 -my-2">
        <s-icon
          name="chevron-down"
          class="opacity-70 transition-transform duration-150"
          :class="{'rotate-180': isOpen}"
          size="1.25rem"
        />
      </span>
    </button>

    <!-- Dropdown -->
    <Teleport to="body">
      <TransitionRoot
        :show="isOpen"
        leave="transition ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
        @after-leave="isOpen = false"
      >
        <div
          ref="dropdownRef"
          :style="floatingStyles"
          @mousedown.stop
          class="z-50 rounded-md shadow-lg overflow-hidden bg-white"
        >
          <s-select-filter-content
            ref="filterContentRef"
            :options="options"
            :disabled="disabled"
            v-model="model"
            @close="closeDropdown"
          >
            <template v-if="$slots.option" #option="{option}">
              <slot name="option" v-bind="{option}" />
            </template>
          </s-select-filter-content>
        </div>
      </TransitionRoot>
    </Teleport>
  </div>
</template>

<script setup lang="ts" generic="T extends Option">
import {computed, nextTick, ref, Teleport, type ComponentPublicInstance} from 'vue';
import {TransitionRoot} from '@headlessui/vue';
import {autoUpdate, flip, offset, shift, size, useFloating} from '@floating-ui/vue';
import {useEventListener} from '@vueuse/core';
import SSelectFilterContent from './SSelectFilterContent.vue';
import SIcon from './SIcon.vue';
import type {Option} from './controls/Option';

const {
  options = [],
  disabled = false,
  placeholder,
} = defineProps<{
  options: T[];
  disabled?: boolean;
  placeholder?: string;
}>();

const model = defineModel<T['value'] | null>();
const isOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const filterContentRef = ref<ComponentPublicInstance<typeof SSelectFilterContent> | null>(null);

const middleware = ref([
  offset(4),
  shift(),
  flip(),
  size({
    apply({rects}) {
      const minWidth = 256; // 16rem = 256px
      const width = Math.max(rects.reference.width, minWidth);
      Object.assign(dropdownRef.value?.style ?? {}, {
        width: `${width}px`,
      });
    },
  }),
]);
const {floatingStyles} = useFloating(triggerRef, dropdownRef, {
  placement: 'bottom-start',
  middleware,
  whileElementsMounted: autoUpdate,
});

const selectedOption = computed(() => {
  if (!model.value) return null;
  return options.find((option) => option.value === model.value) || null;
});

const toggleDropdown = async () => {
  if (disabled) return;

  isOpen.value = !isOpen.value;

  if (isOpen.value) {
    await nextTick();
    setTimeout(() => {
      if (filterContentRef.value) {
        // Auto-focus the search input when opening the dropdown
        const searchInput = filterContentRef.value.$el?.querySelector('input');
        if (searchInput) {
          (searchInput as HTMLInputElement).focus();
        }
      }
    }, 50);
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  nextTick(() => {
    if (triggerRef.value) {
      // Return focus to the trigger button when dropdown closes
      (triggerRef.value as HTMLElement).focus();
    }
  });
};

// Close dropdown when clicking outside
const handleClickOutside = (event: Event) => {
  if (isOpen.value && triggerRef.value && !triggerRef.value.contains(event.target as Node)) {
    closeDropdown();
  }
};

// Close dropdown when Escape key is pressed
const handleKeydown = (event: KeyboardEvent) => {
  if (isOpen.value && event.key === 'Escape') {
    closeDropdown();
  }
};

useEventListener(document, 'click', handleClickOutside);
useEventListener(document, 'keydown', handleKeydown);
</script>
